import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { LanguageMode, Settings } from '../types';
import { readJson, STORAGE_KEYS, writeJson } from '../services/storage';

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const DEFAULT_SETTINGS: Settings = {
  languageMode: 'ar',
  easyMode: true,
  fontSize: 'large', // the app defaults to Large, as requested
  highContrast: false,
  reducedMotion: false,
  speechSpeed: 'default', // slightly slower than normal
  autoSpeakQuestion: true,
  speakFeedback: true,
  ttsEngine: 'web-speech',
  piperUrl: 'http://localhost:5959',
  arabicVoiceUri: null,
  englishVoiceUri: null,
  onboardingComplete: false,
};

interface SettingsContextValue {
  settings: Settings;
  update: (patch: Partial<Settings>) => void;
  reset: () => void;
  /** 'ar' for both Arabic modes, 'en' for English-only. Used for UI chrome. */
  uiLang: 'ar' | 'en';
  /** True when the interface should be laid out right-to-left. */
  rtl: boolean;
  /** True when the original English should be shown under the Arabic. */
  showEnglish: boolean;
  /** True when Arabic should be shown at all. */
  showArabic: boolean;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

function loadSettings(): Settings {
  const stored = readJson<Partial<Settings>>(STORAGE_KEYS.settings, {});
  return {
    ...DEFAULT_SETTINGS,
    reducedMotion: prefersReducedMotion(),
    ...stored,
  };
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  const update = useCallback((patch: Partial<Settings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      writeJson(STORAGE_KEYS.settings, next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    const fresh = { ...DEFAULT_SETTINGS, reducedMotion: prefersReducedMotion() };
    setSettings(fresh);
    writeJson(STORAGE_KEYS.settings, fresh);
  }, []);

  // Keep <html> in sync: language, direction, text size, contrast, motion.
  useEffect(() => {
    const root = document.documentElement;
    const mode: LanguageMode = settings.languageMode;
    const rtl = mode !== 'en';
    root.lang = rtl ? 'ar' : 'en';
    root.dir = rtl ? 'rtl' : 'ltr';
    root.dataset.fontSize = settings.fontSize;
    root.dataset.contrast = settings.highContrast ? 'high' : 'normal';
    root.dataset.motion = settings.reducedMotion ? 'reduced' : 'full';
    root.dataset.easy = settings.easyMode ? 'on' : 'off';
  }, [settings.languageMode, settings.fontSize, settings.highContrast, settings.reducedMotion, settings.easyMode]);

  const value = useMemo<SettingsContextValue>(() => {
    const rtl = settings.languageMode !== 'en';
    return {
      settings,
      update,
      reset,
      uiLang: rtl ? 'ar' : 'en',
      rtl,
      showEnglish: settings.languageMode !== 'ar',
      showArabic: settings.languageMode !== 'en',
    };
  }, [settings, update, reset]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside <SettingsProvider>');
  return ctx;
}
